import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  DialogContent,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Fade,
  CircularProgress,
  Alert,
  Slide,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { Visibility, VisibilityOff, Lock, Close, PasswordOutlined } from '@mui/icons-material';
import {
  AuthTextField,
  PrimaryButton,
  StyledDialog,
  DialogHeaderStyled,
  StyledCloseButton,
  CancelButton,
} from '../../../utils/common/styles/commonStyles';
import { useDispatch, useSelector } from 'react-redux';
import { changeLoading } from '../store/auth.selectors';
import { changePassword } from '../store/auth.actions';
import PropTypes from 'prop-types';

// Animations
const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Transition component for modal
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Styled Components
const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(4),
  position: 'relative',
  '& input[type="password"]': {
    // Hide Edge reveal password icon
    '&::-ms-reveal': {
      display: 'none',
    },
    // Hide Chrome/Edge autofill icons
    '&::-webkit-contacts-auto-fill-button': {
      display: 'none',
    },
    '&::-webkit-caps-lock-indicator': {
      display: 'none',
    },
  },
}));

const FormContainer = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  // Hide Edge/Chrome password field icons
  '& input[type="password"]': {
    '&::-ms-reveal': {
      display: 'none',
    },
    '&::-webkit-contacts-auto-fill-button': {
      display: 'none',
    },
  },
});

const ChangePasswordModal = ({ open, setOpen }) => {
  const dispatch = useDispatch();
  const loading = useSelector(changeLoading);

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [showAlert, setShowAlert] = useState({
    open: false,
    message: '',
    severity: '',
  });

  const validationSchema = Yup.object({
    oldPassword: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .required('Current password is required'),
    newPassword: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'Password must contain at least one uppercase letter, one lowercase letter, and one number'
      )
      .required('New password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
      .required('Please confirm your new password'),
  });

  const formik = useFormik({
    initialValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setShowAlert((prev) => ({ ...prev, open: false }));
      const result = await dispatch(changePassword(values));

      if (result.status === 'success') {
        formik.resetForm();
        setShowPasswords({ current: false, new: false, confirm: false });
        setShowAlert({
          open: true,
          message: 'Password changed successfully!',
          severity: 'success',
        });
      } else {
        setShowAlert({
          open: true,
          message: result.message || 'Failed to change password. Please try again.',
          severity: 'error',
        });
      }
    },
  });

  const handleTogglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleClose = () => {
    formik.resetForm();
    setShowAlert((prev) => ({ ...prev, open: false }));
    setShowPasswords({ current: false, new: false, confirm: false });
    setOpen(false);
  };

  const createPasswordInputProps = (field, showPassword) => ({
    startAdornment: (
      <InputAdornment position="start">
        <Lock color="action" />
      </InputAdornment>
    ),
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          onClick={() => handleTogglePasswordVisibility(field)}
          edge="end"
          size="small"
          sx={{
            color: 'action.active',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  });

  const handleAlertClose = (reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowAlert((prev) => ({ ...prev, open: false }));
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      slots={{ transition: Transition }}
      keepMounted
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: 480,
          width: '100%',
          animation: `${slideUp} 0.4s ease-out`,
          margin: '16px',
        },
      }}
    >
      <DialogHeaderStyled
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <PasswordOutlined />
        Change Password
        <StyledCloseButton onClick={handleClose} size="small">
          <Close />
        </StyledCloseButton>
      </DialogHeaderStyled>

      <StyledDialogContent>
        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            textAlign: 'center',
            marginBottom: 3,
            fontWeight: 500,
          }}
        >
          Please enter your current password and choose a new secure password.
        </Typography>

        <FormContainer onSubmit={formik.handleSubmit}>
          <AuthTextField
            fullWidth
            label="Current Password"
            type={showPasswords.current ? 'text' : 'password'}
            id="oldPassword"
            name="oldPassword"
            autoComplete="current-password"
            value={formik.values.oldPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.oldPassword && Boolean(formik.errors.oldPassword)}
            helperText={formik.touched.oldPassword && formik.errors.oldPassword}
            slotProps={{
              input: createPasswordInputProps('current', showPasswords.current),
            }}
            sx={{ marginBottom: '16px' }}
          />

          <AuthTextField
            fullWidth
            label="New Password"
            type={showPasswords.new ? 'text' : 'password'}
            id="newPassword"
            name="newPassword"
            autoComplete="new-password"
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
            helperText={formik.touched.newPassword && formik.errors.newPassword}
            slotProps={{
              input: createPasswordInputProps('new', showPasswords.new),
            }}
            sx={{ marginBottom: '16px' }}
          />

          <AuthTextField
            fullWidth
            label="Confirm New Password"
            type={showPasswords.confirm ? 'text' : 'password'}
            id="confirmPassword"
            name="confirmPassword"
            autoComplete="new-password"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
            helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
            slotProps={{
              input: createPasswordInputProps('confirm', showPasswords.confirm),
            }}
            sx={{ marginBottom: '16px' }}
          />

          <Fade in={showAlert.open} timeout={300}>
            <Box mb={3}>
              <Alert
                severity={showAlert.severity}
                sx={{
                  marginTop: 2,
                  borderRadius: 2,
                  '& .MuiAlert-message': {
                    fontWeight: 500,
                  },
                }}
                onClose={handleAlertClose}
              >
                {showAlert.message}
              </Alert>
            </Box>
          </Fade>

          <Box
            display="flex"
            gap={2}
            justifyContent="center"
            flexDirection={{ xs: 'column', sm: 'row' }}
          >
            <CancelButton onClick={handleClose} sx={{ order: { xs: 2, sm: 1 } }}>
              Cancel
            </CancelButton>

            <PrimaryButton
              type="submit"
              variant="contained"
              disabled={loading || !formik.isValid || formik.isSubmitting}
              sx={{ order: { xs: 1, sm: 2 } }}
            >
              {loading || formik.isSubmitting ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} color="inherit" />
                  <span>Updating...</span>
                </Box>
              ) : (
                'Update'
              )}
            </PrimaryButton>
          </Box>
        </FormContainer>
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default ChangePasswordModal;

ChangePasswordModal.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
};
